import { useState, useEffect } from "react";

export default function NewTemplateModal({
  open,
  onClose,
  onCreate,
  initialData,
  isEdit = false,
  loading = false,
}) {
  const [name, setName] = useState(initialData?.name || "");
  const [type, setType] = useState(initialData?.type || "sms");
  const [subject, setSubject] = useState(initialData?.subject || "");
  const [body, setBody] = useState(initialData?.body || "");
  const [variables, setVariables] = useState(
    initialData?.variables?.join(", ") || ""
  );
  const [isActive, setIsActive] = useState(initialData?.isActive ?? true);
  const [error, setError] = useState("");

  // Populate form with initial data for editing
  useEffect(() => {
    if (isEdit && initialData) {
      setName(initialData.name || "");
      setType(initialData.type || "sms");
      setSubject(initialData.subject || "");
      setBody(initialData.body || "");
      setVariables(initialData.variables?.join(", ") || "");
      setIsActive(initialData.isActive ?? true);
    } else {
      // Reset form for new template
      setName("");
      setType("sms");
      setSubject("");
      setBody("");
      setVariables("");
      setIsActive(true);
    }
  }, [isEdit, initialData, open]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) {
      setError("Template name is required");
      return;
    }
    if (!type) {
      setError("Type is required");
      return;
    }
    if (!body.trim()) {
      setError("Message body is required");
      return;
    }
    const templateData = {
      name: name.trim(),
      type,
      subject: subject.trim(),
      body: body.trim(),
      variables: variables
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v),
      isActive,
    };
    try {
      await onCreate(templateData);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to save template");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4">
      <div className="bg-[#131313] rounded-lg shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto p-8 border border-[#131313]">
        <h2 className="text-2xl text-gray-200 mb-6">
          {isEdit ? "Edit Template" : "Create New Template"}
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-300 mb-2">Template Name</label>
            <input
              type="text"
              className="w-full p-3 rounded bg-[#131313] border border-[#333333] text-white"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-300 mb-2">Type</label>
            <select
              className="w-full p-3 rounded bg-[#131313] border border-[#333333] text-white"
              value={type}
              onChange={(e) => setType(e.target.value)}
              required
            >
              <option value="sms">SMS</option>
              <option value="email">Email</option>
              <option value="push">Push Notification</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-gray-300 mb-2">Subject</label>
            <input
              type="text"
              className="w-full p-3 rounded bg-[#131313] border border-[#333333] text-white"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-300 mb-2">Message Body</label>
            <textarea
              className="w-full p-3 rounded bg-[#131313] border border-[#333333] text-white"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              required
              rows={3}
            />
          </div>
          <div className="mb-6">
            <label className="block text-gray-300 mb-2">
              Variables Used{" "}
              <span className="text-xs text-gray-400">(comma separated)</span>
            </label>
            <input
              type="text"
              className="w-full p-3 rounded bg-[#131313] border border-[#333333] text-white"
              value={variables}
              onChange={(e) => setVariables(e.target.value)}
              placeholder="e.g. Name, Amount, Date"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-300 mb-2">Is Active</label>
            <input
              type="checkbox"
              className="w-4 h-4 text-yellow-400 focus:ring-yellow-400 border-gray-600 rounded"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
          </div>
          {error && <div className="text-red-500 mb-2">{error}</div>}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              className="px-4 py-2 rounded bg-gray-700 text-gray-200 border border-gray-600 hover:bg-gray-800"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded bg-yellow-400 text-black font-bold hover:bg-yellow-500"
              disabled={loading}
            >
              {loading
                ? isEdit
                  ? "Updating..."
                  : "Creating..."
                : isEdit
                ? "Update"
                : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
